/* eslint-disable sonarjs/no-duplicate-string */
import { FC, useContext } from "react";

import { FormType, PeerToPeerQueryParams } from "@simplyk/common";

import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";

import { DonationFormV2CheckoutFooter } from "./CheckoutFooter/DonationFormV2CheckoutFooter";
import { CampaignIsLiveDialog } from "./PeerToPeer/CampaignIsLiveDialog/CampaignIsLiveDialog";
import { useCampaignCreatedSuccess } from "./PeerToPeer/CampaignIsLiveDialog/useCampaignCreatedSuccess";
import { FundraiseDialog } from "./PeerToPeer/FundraiseDialog/FundraiseDialog";

import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useShouldHideFundraise } from "@/features/FormV2/hooks/useShouldHideFundraise";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { ThemedHostedBy } from "@/features/FormV2/sharedComponents/HostedBy/ThemedHostedBy";
import { ThemedDonor } from "@/features/FormV2/sharedComponents/ThemedDonor";
import { ThemedDonorsListBlock } from "@/features/FormV2/sharedComponents/ThemedDonorsListBlock";
import { ThemedDonorsListDialog } from "@/features/FormV2/sharedComponents/ThemedDonorsListDialog";
import {
  useThemedDonorListDialogState,
  useThemedDonorListDialogServerState,
} from "@/features/FormV2/sharedComponents/useThemedDonorListDialogState";
import { useThemedDonorsListBlockState } from "@/features/FormV2/sharedComponents/useThemedDonorsListBlockState";
import { Amounts } from "@/features/FormV2/Step1/DonationForm/Amounts";
import { useDonationFirstStepSubmit } from "@/features/FormV2/Step1/DonationForm/useDonationFirststepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { PeerToPeerIndividualCampaignFormV2Summary } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerIndividualCampaignFormV2Summary";
import { PeerToPeerIndividualCampaignFundraiseCampaignAction } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerIndividualCampaignFundraiseCampaignAction";
import { PeerToPeerRegisterCampaignAction } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerRegisterCampaignAction";
import { useOpenPeerToPeerRegistrationForm } from "@/features/FormV2/Step1/PeerToPeer/useOpenPeerToPeerRegistrationForm";
import { PeerToPeerFormV2IndividualCampaignStep1Layout } from "@/features/FormV2/Step1/PeerToPeerFormV2IndividualCampaignStep1Layout";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { useQueryState, parseAsBoolean } from "@/hooks/useQueryState";

export type PeerToPeerFormV2IndividualCampaignStep1Props = {
  isEmbed?: boolean;
};

export const PeerToPeerFormV2IndividualCampaignStep1: FC<PeerToPeerFormV2IndividualCampaignStep1Props> = ({
  isEmbed = false,
}) => {
  const { formObject, hasBanner, formCategory } = useFormV2Context(FormType.DonationForm);
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { isPreview } = usePreviewContext();
  const { canSubmitFirstStep } = useDonationFirstStepSubmit();
  const utm = useUtmForm();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.DonationForm,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const [fundraiseQuery, setFundraiseQuery] = useQueryState(
    PeerToPeerQueryParams.OpenFundraiser,
    parseAsBoolean.withDefault(false).withOptions({ history: "push" })
  );

  const { isCampaignIsLiveDialogOpen, handleCampaignIsLiveDialogClose, formLink } = useCampaignCreatedSuccess(false);

  const { hasDonations, hasMoreDonations, latestDonations } = useThemedDonorsListBlockState({
    donationFormId: formObject.id,
  });

  const {
    open: donorsListDialogOpen,
    onOpen: onDonorsListDialogOpen,
    onClose: onDonorsListDialogClose,
  } = useThemedDonorListDialogState();

  const donorsDialogServerState = useThemedDonorListDialogServerState({
    donationFormId: formObject.id,
    open: donorsListDialogOpen,
  });

  const hasMandatoryRegistration = formObject.campaign?.hideFundraiseButton ?? formObject.hideFundraiseButton;
  const shouldHideFundraise = useShouldHideFundraise();
  const shouldShowFundraiseButton =
    (formObject.campaign?.allowTeamCreation || formObject.campaign?.allowFundraiserCreation) && !shouldHideFundraise;

  const openPeerToPeerRegistrationForm = useOpenPeerToPeerRegistrationForm(formObject.ticketingId ?? undefined);

  return (
    <HeartsAnimationProvider>
      <PeerToPeerFormV2IndividualCampaignStep1Layout
        isEmbed={isEmbed}
        contextDrawerHeight={isExpanded ? bannerHeight : 0}
        slots={{
          banner: hasBanner ? <MobileFormV2Banner /> : undefined,
          summary: <PeerToPeerIndividualCampaignFormV2Summary forceInlined={isEmbed} />,
          hostedBy: (
            <ThemedHostedBy
              campaignId={formObject.campaignId!}
              teamId={formObject.teamId ?? undefined}
              isIndividualCampaignInTeamCampaign={!!formObject.teamId}
            />
          ),
          donors: hasDonations && (
            <ThemedDonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
              {latestDonations.map((donation, index) => (
                <ThemedDonor {...donation} key={index} />
              ))}
            </ThemedDonorsListBlock>
          ),
          helpFundraise: hasMandatoryRegistration ? (
            <PeerToPeerRegisterCampaignAction
              canCreateTeamCampaigns={!!formObject.campaign?.allowTeamCreation}
              canCreateIndividualCampaigns={!!formObject.campaign?.allowFundraiserCreation}
              onClick={openPeerToPeerRegistrationForm}
            />
          ) : (
            shouldShowFundraiseButton && (
              <PeerToPeerIndividualCampaignFundraiseCampaignAction onClick={() => setFundraiseQuery(true)} />
            )
          ),
          donationAmounts: <Amounts />,
          generateReceipt: <FormV2GenerateReceiptInfobox />,
          zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
          checkoutFooter: <DonationFormV2CheckoutFooter />,
        }}
        showCheckoutFooter={canSubmitFirstStep && !isPreview}
        logoHref={logoHref}
        onLogoClick={onTrackLogoClick}
      />
      <FundraiseDialog
        open={fundraiseQuery}
        onClose={() => setFundraiseQuery(false)}
        campaignId={formObject.campaignId as string}
        allowTeamCreation={Boolean(formObject.campaign?.allowTeamCreation)}
        allowFundraiserCreation={Boolean(formObject.campaign?.allowFundraiserCreation)}
        allowJoinTeam={Boolean(formObject.campaign?.allowJoinTeam)}
      />
      {hasDonations && (
        <ThemedDonorsListDialog
          {...donorsDialogServerState}
          open={donorsListDialogOpen}
          onClose={onDonorsListDialogClose}
        />
      )}
      {/* Display the campaign is live dialog if the p2p campaign is created successfully */}
      <CampaignIsLiveDialog
        open={isCampaignIsLiveDialogOpen}
        onClose={handleCampaignIsLiveDialogClose}
        isTeamCampaign={false}
        isIndividualCampaign={true}
        fundraiserName={formObject.donationFormFields?.[0]?.title}
        formLink={formLink}
      />
    </HeartsAnimationProvider>
  );
};
